// ============================================================================
// javaslat.js — a "✨ Javaslat (AI)" gomb közös viselkedése (2026-10-04, tagi
// kérésre: minden többnyelvű megadásnál legyen javaslat-gomb, ami a Gemini
// Flash-sel kitölti az üres mezőket).
//
// A gomb CSAK JAVASOL: beírja az üres mezőkbe, de nem ment - az ember átnézi,
// javítja, és ő menti el (egy név/cím fordításának több jó változata lehet).
//
// Két szándékos szabály:
//  1. CSAK az ÜRES mezőket tölti ki, és csak olyat, ami a válasz megérkezésekor
//     MÉG üres - amit közben beírtál, azt nem írja felül.
//  2. Ha a válasz megérkezésekor a szerkesztő már nincs a képernyőn (bezártad,
//     vagy másik szobát szerkesztesz), a javaslatot ELDOBJA - különben egy
//     másik szoba állapotába írna.
// ============================================================================

import { t } from "./i18n.js";
import { log } from "./log.js";

/** A szerver hibakódjából érthető felirat-kulcs. */
export function hibaKulcs(err) {
  const kod = String((err && err.code) || "");
  if (kod === "functions/resource-exhausted") return "suggest.limit";
  if (kod === "functions/permission-denied") return "suggest.noAccess";
  return "suggest.failed";
}

/**
 * A javaslat FORRÁSA (2026-10-20, v122; Tamás: "a meg nem adott nyelvekre kell javaslatot tenni az ELSŐNEK megadott név alapján"):
 *  - ha az alap név ki van töltve, az a forrás, a NYELVE ISMERETLEN (az admin nem feltétlenül a saját nyelvén írta: egy japánul megadott
 *    név ne számítson magyarnak, és a magyar megfelelője is elkészüljön) - a fordító felismeri;
 *  - ha az alap név üres, de egy nyelvi mező ki van töltve, az ELSŐ kitöltött nyelvi mező a forrás, a nyelve a mező nyelve.
 * @param {string} alapNev az alap név mező értéke
 * @param {Array<{kod: string, ertek: string}>} nyelviMezok a nyelvi mezők, a megjelenés sorrendjében
 * @returns {{alap: string, alapNyelv: string|undefined}}
 */
export function javaslatForras(alapNev, nyelviMezok) {
  const alap = String(alapNev || "").trim();
  if (alap) return { alap, alapNyelv: undefined };
  const elso = (nyelviMezok || []).find((m) => m && String(m.ertek || "").trim());
  return elso ? { alap: String(elso.ertek).trim(), alapNyelv: elso.kod } : { alap: "", alapNyelv: undefined };
}

/**
 * @param {object} p
 * @param {string} p.uiLang
 * @param {() => {alap: string, alapNyelv?: string, mezok: Array<{kod: string, ures: boolean}>}} p.gyujt
 *   A KATTINTÁS pillanatában kérdezi le az állapotot (a beviteli mezők aktuális
 *   értékeit) - nem a kirajzoláskoriat.
 * @param {(alap: string, kodok: string[], alapNyelv?: string) => Promise<Record<string, string>>} p.kerj
 * @param {(kod: string, ertek: string) => boolean} p.alkalmaz
 *   Beírja az értéket; igazzal tér vissza, ha tényleg beírta (üres volt még).
 * @param {() => boolean} [p.ervenyes] a válasz megérkezésekor még az a szerkesztő van-e nyitva
 * @param {string} [p.osztaly]
 * @returns {{elem: HTMLElement, gomb: HTMLButtonElement, allapot: HTMLElement}}
 */
export function javaslatSor({ uiLang, gyujt, kerj, alkalmaz, ervenyes = () => true, osztaly = "neutral-btn compact" }) {
  const elem = document.createElement("div");
  elem.className = "suggest-row";

  const gomb = document.createElement("button");
  gomb.type = "button";
  gomb.className = `${osztaly} suggest-btn`;
  const alapFelirat = "✨ " + t(uiLang, "suggest.button");
  gomb.textContent = alapFelirat;

  const allapot = document.createElement("div");
  allapot.className = "suggest-status";
  allapot.setAttribute("role", "status");

  const jelez = (kulcs, params, hiba = false) => {
    allapot.textContent = kulcs ? t(uiLang, kulcs, params) : "";
    allapot.classList.toggle("error", hiba);
  };

  let dolgozik = false;
  gomb.addEventListener("click", async () => {
    if (dolgozik) return;
    const { alap, alapNyelv, mezok } = gyujt();
    if (!alap) { jelez("suggest.needBase", null, true); return; }
    const kodok = mezok.filter((m) => m.ures).map((m) => m.kod);
    if (kodok.length === 0) { jelez("suggest.nothingToDo"); return; }

    dolgozik = true;
    gomb.disabled = true;
    gomb.textContent = "⏳ " + t(uiLang, "suggest.working");
    jelez(null);
    try {
      const javaslatok = await kerj(alap, kodok, alapNyelv);
      if (!elem.isConnected || !ervenyes()) return; // közben elhagyta a szerkesztőt
      let beirt = 0;
      let hianyzo = 0; // a kért nyelvek közül, amelyikre a szerver NEM adott használható javaslatot
      for (const kod of kodok) {
        const ertek = javaslatok && typeof javaslatok[kod] === "string" ? javaslatok[kod].trim() : "";
        if (!ertek) hianyzo++;
        else if (alkalmaz(kod, ertek)) beirt++;
      }
      // Részleges eredménynél (2026-10-15): jelezzük, hány nyelv maradt ki - a gomb újbóli megnyomása pótolja őket
      // (csak az üres mezőket kéri újra).
      if (beirt > 0 && hianyzo > 0) jelez("suggest.partial", { n: beirt, m: hianyzo });
      else if (beirt > 0) jelez("suggest.done", { n: beirt });
      else jelez("suggest.failed", null, true);
    } catch (err) {
      log.error("[javaslat] hiba:", err);
      if (elem.isConnected) jelez(hibaKulcs(err), null, true);
    } finally {
      dolgozik = false;
      gomb.disabled = false;
      gomb.textContent = alapFelirat;
    }
  });

  elem.appendChild(gomb);
  elem.appendChild(allapot);
  return { elem, gomb, allapot };
}
